import { APP_BUILDER_OPERATIONS, PERM_MOD_APP_BUILDER } from 'src/constants';
import { useFeatureFlags } from 'src/context';
import { useAppSelector } from 'src/hooks/useStore';
import * as UserUtils from 'src/utils/UserUtils';

/**
 * Whether this workspace gets the app builder. The enable_app_builder flag
 * stays the rollout switch, but builder access also follows the plan — read
 * from the plan-permission limit the backend publishes (plan_permissions.go),
 * the same shape as {@link useAuditLogGate}. The backend grants it on the
 * plans credits follow: Studio, the self-serve *_platform catalog, and
 * Enterprise. Legacy-plan workspaces keep the legacy add-app flow even with
 * the flag on, so the flag can never expose a credit-metered surface to a
 * workspace that can't be granted credits.
 */
export const useAppBuilderAccess = (): {
  /** The effective gate: feature flag AND plan entitlement. */
  enableAppBuilder: boolean;
  hasAppBuilderPlanAccess: boolean;
  /**
   * False until user data has loaded. Redirect-style enforcement should wait
   * for this so an eligible workspace isn't bounced off the builder while its
   * permissions are still in flight.
   */
  isPlanAccessResolved: boolean;
} => {
  const { EnableAppBuilder } = useFeatureFlags();
  const permissions = useAppSelector((state) => state.user.data?.permissions);
  const isUserLoaded = useAppSelector((state) => state.user.loaded);

  const appBuilderOperation = UserUtils.getOperation(
    PERM_MOD_APP_BUILDER,
    APP_BUILDER_OPERATIONS.USE,
    permissions,
  );
  const hasAppBuilderPlanAccess = (appBuilderOperation?.planLimit ?? 0) > 0;

  return {
    enableAppBuilder: EnableAppBuilder && hasAppBuilderPlanAccess,
    hasAppBuilderPlanAccess,
    isPlanAccessResolved: isUserLoaded,
  };
};
